import { deriveCameraGraphNavigationPhase } from '@toolproof-core/visualization/_pure/camera-graph';
import type { SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toCameraViewId } from './cameraGraph';
import type { WorkflowCameraDirectionEffect } from '@/workflow-runner/_contract/_~generated/cameraDirectionEffect';

export function derivePanelCameraIngress(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    inputType?: string;
    outputType?: string;
    direction: 'forward' | 'reverse';
    transitionKey: string;
    policy: Extract<WorkflowCameraDirectionEffect, { cue: 'panel-ingress' }>;
}): TransitionPhase {
    const { frame, stepKey, policy } = args;
    const camera = frame.spaceRenderOptions?.camera;
    const output = policy.target === 'output' || policy.target === 'directional' && args.direction === 'reverse';
    const preferredType = output ? args.outputType : args.inputType;
    const preferredView = preferredType && toCameraViewId(stepKey, preferredType, 'folded');
    const view = preferredView && camera?.graph?.views[preferredView] ? preferredView
        : Object.keys(camera?.graph?.views ?? {}).find(id =>
            id.startsWith(`workflow:${encodeURIComponent(stepKey)}:`) && id.endsWith(':folded'));
    if (!camera || !view) throw new Error(`Guided panel ingress could not resolve the input station for ${stepKey}.`);
    const navigation = deriveCameraGraphNavigationPhase({
        ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera, activeView: view } },
    }, undefined);
    if (!navigation) throw new Error(`Guided panel ingress could not navigate to ${view}.`);
    return {
        patch: {},
        cameraNavigation: navigation.cameraNavigation && {
            ...navigation.cameraNavigation, orbitCenter: policy.orbit === 'origin' ? [0, 0, 0] : undefined, arrival: policy.arrival,
        },
        transitionOptions: {
            transitionKind: 'interpolated',
            transitionKey: `runner-camera-ingress:${args.transitionKey}:${args.direction}`,
            duration: camera.navigationDurationMs ?? 0,
            easing: policy.easing,
        },
    };
}
